// @flow

import { useMemo } from 'react';
import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useUniqueTableName from '../../../../hooks/useUniqueTableName';
import { formatBedsBathLabel, formatCreatedByName } from '../utils';
import type {
  GrossRentChangeFloorPlan,
  GrossRentChangeFloorPlanResult,
  UseGrossRentChangeFloorPlanTableProps,
} from '../types';
import { HEADERS, NAME, PROPERTY_PATH_MAP } from './constants';

const toFloorPlanResults = (
  floorPlans: GrossRentChangeFloorPlan[],
): GrossRentChangeFloorPlanResult[] =>
  floorPlans.map((floorPlan) => ({
    ...floorPlan,
    bedsBath: formatBedsBathLabel(
      floorPlan.numberOfBedrooms,
      floorPlan.numberOfBathrooms,
    ),
    createdByName: formatCreatedByName(floorPlan.createdBy),
  }));

export const useGrossRentChangeFloorPlanTable = ({
  floorPlans,
  effectiveDate,
}: UseGrossRentChangeFloorPlanTableProps) => {
  const name = useUniqueTableName(`${NAME}-${effectiveDate}`);

  const floorPlanResults = useMemo(
    () => toFloorPlanResults(floorPlans),
    [floorPlans],
  );

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers: HEADERS,
    initialOrderBy: 'floorPlanName',
    initialOrder: 'ASC',
  });

  const sortedFloorPlans = useTableFilterSortData({
    results: floorPlanResults,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  useTableManageColumns({
    name,
    headers: HEADERS,
    firstRow: sortedFloorPlans[0],
  });

  return {
    floorPlans: sortedFloorPlans,
  };
};
